// src/cockpit4/components/HumanAICollaboration.tsx
// ============================================
// 人机协同优化 - 工作流中心 P1-功能6
//
// 功能：
//   1. 哪些环节适合AI、哪些适合人（按节点执行方分类：AI / 人工 / 混合）
//   2. AI替代率统计（自动化执行占比 + 节点分布饼图）
//   3. 人力节省测算（AI节省时间 + 预估节省成本，可配置人工时薪）
//   4. 人机协作最佳实践（内置场景库：内容生成/客户审批/数据查询/创意策划/质量审核）
//   5. 人工节点优化建议（基于人工节点成功率、耗时、AI基准生成数据驱动建议）
//   6. 协作效率分析（节点级 AI vs 人工 耗时对比柱状图 + 人力节省趋势图）
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   - 节点执行记录：localStorage(key: xeaos_workflow_executions) 读取 WorkflowExecution[]
//     （与 WorkflowMonitor 同源，分析每个节点的 AI/人工执行情况）
//   - 协同配置：localStorage(key: xeaos_human_ai_config) 持久化人工时薪
//   - 执行方判定：由节点 nodeType 推断
//       ai_call    → ai     （AI 模型调用，纯智能执行）
//       approval   → human  （人工审批/决策，纯人工执行）
//       其余类型   → hybrid （系统按人工配置规则自动执行，人机协同）
//   - AI vs 人工对比基准：当某节点只有一种执行方时，另一侧使用全局真实基准
//     （纯AI节点的平均耗时/成功率、纯人工节点的平均耗时/成功率，均由真实记录聚合）
//   无数据时显示"暂无执行数据"，绝不展示假数据
//
// 建议规则引擎 generateRecommendation：
//   - AI成功率>=90% 且 AI比人工快 → 建议 ai（全自动化）
//   - 人工成功率>=95% 且 AI成功率<70% → 建议 human（人工处理）
//   - 其余 → 建议 hybrid（AI初审+人工复审）
//   扩展守卫：无人工基准（humanAvgDuration===0）且 AI成功率>=90% 时直接建议 ai，
//   避免无对照数据时误判为混合。
// ============================================

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertCircle,
  BarChart3,
  Bot,
  CheckCircle2,
  ClipboardList,
  Clock,
  Coins,
  Cpu,
  Gauge,
  Layers,
  Lightbulb,
  PieChart as PieChartIcon,
  RefreshCw,
  Sparkles,
  TrendingUp,
  UserCog,
  Wallet,
  Zap,
  type LucideIcon,
} from "lucide-react";

// ============================================
// 类型定义（与改进计划文档数据模型对齐）
// ============================================

/** 执行方类型 */
export type ExecutorType = "ai" | "human" | "hybrid";

/** 节点协同分析（单节点 AI vs 人工对比） */
export interface NodeCollaboration {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  executorType: ExecutorType;
  // AI 执行统计
  aiExecutions: number;
  aiSuccessRate: number; // 0..1
  aiAvgDuration: number; // 毫秒
  // 人工执行统计
  humanExecutions: number;
  humanSuccessRate: number; // 0..1
  humanAvgDuration: number; // 毫秒
  // 对比
  aiFasterThanHuman: boolean;
  timeSaved: number; // AI 节省时间（分钟）
  recommendation: ExecutorType;
  recommendationReason: string;
}

/** 协同统计概览 */
export interface CollaborationStats {
  totalNodes: number;
  aiNodes: number;
  humanNodes: number;
  hybridNodes: number;
  aiAutomationRate: number; // 0..1
  totalTimeSaved: number; // 小时
  estimatedCostSaved: number; // 元
  avgHumanHourlyCost: number; // 元/小时
}

/** 最佳实践 */
export interface BestPractice {
  id: string;
  scenario: string;
  recommendedExecutor: ExecutorType;
  reason: string;
  example: string;
}

/** 人工时薪配置（持久化） */
interface HumanAiConfig {
  avgHumanHourlyCost: number;
}

/** 节点执行记录（与 WorkflowMonitor 同源，本地声明以保持自包含） */
type ExecutionStatus =
  | "running"
  | "success"
  | "failed"
  | "queued"
  | "timeout"
  | "cancelled";

interface NodeExecution {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  status: ExecutionStatus;
  startTime: string;
  endTime?: string;
  duration?: number; // 毫秒
}

interface WorkflowExecution {
  id: string;
  workflowName: string;
  status: ExecutionStatus;
  startTime: string;
  nodeExecutions: NodeExecution[];
}

/** 趋势数据点 */
interface TrendPoint {
  date: string;
  label: string;
  savedHours: number;
}

/** 人工节点优化建议项 */
interface HumanNodeSuggestion {
  nodeId: string;
  nodeName: string;
  suggestion: string;
  priority: "high" | "medium" | "low";
}

// ============================================
// 常量
// ============================================

const EXECUTIONS_KEY = "xeaos_workflow_executions";
const CONFIG_KEY = "xeaos_human_ai_config";

/** 执行方展示配置 */
const EXECUTOR_CONFIG: Record<
  ExecutorType,
  { label: string; color: string; bg: string; border: string }
> = {
  ai: { label: "AI", color: "#60A5FA", bg: "bg-sky-500/10", border: "border-sky-500/30" },
  human: { label: "人工", color: "#FBBF24", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  hybrid: { label: "混合", color: "#A78BFA", bg: "bg-violet-500/10", border: "border-violet-500/30" },
};

/** 节点类型 → 执行方 映射（其余默认 hybrid） */
const NODE_TYPE_EXECUTOR: Record<string, ExecutorType> = {
  ai_call: "ai",
  approval: "human",
};

/** 默认人工时薪（元/小时） */
const DEFAULT_HOURLY_COST = 100;

/** 内置最佳实践库 */
const BEST_PRACTICES: BestPractice[] = [
  { id: "bp_content", scenario: "内容生成", recommendedExecutor: "ai", reason: "AI生成速度快，质量稳定", example: "公众号文章、短视频脚本" },
  { id: "bp_approval", scenario: "客户审批", recommendedExecutor: "human", reason: "涉及商业判断，需人工决策", example: "大额报价审批、合同签署" },
  { id: "bp_query", scenario: "数据查询", recommendedExecutor: "ai", reason: "结构化查询AI更高效", example: "收入查询、客户列表、项目进度" },
  { id: "bp_creative", scenario: "创意策划", recommendedExecutor: "hybrid", reason: "AI出方案+人工优化", example: "活动策划、品牌定位" },
  { id: "bp_review", scenario: "质量审核", recommendedExecutor: "hybrid", reason: "AI初审+人工复审", example: "内容审核、代码审查" },
];

/** 已完成状态集合（用于成功率/耗时统计） */
const COMPLETED_STATUSES: ExecutionStatus[] = ["success", "failed", "timeout", "cancelled"];
/** 可计入耗时的状态（排除 cancelled/running/queued 的不可靠耗时） */
const DURATION_STATUSES: ExecutionStatus[] = ["success", "failed", "timeout"];

// ============================================
// 工具函数
// ============================================

/** 推断节点执行方 */
function classifyExecutor(nodeType: string): ExecutorType {
  return NODE_TYPE_EXECUTOR[nodeType] || "hybrid";
}

/** 读取执行记录 */
function loadExecutions(): WorkflowExecution[] {
  try {
    const raw = localStorage.getItem(EXECUTIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as WorkflowExecution[]) : [];
  } catch {
    return [];
  }
}

/** 读取协同配置（无则写默认） */
function loadConfig(): HumanAiConfig {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) {
      const def = { avgHumanHourlyCost: DEFAULT_HOURLY_COST };
      localStorage.setItem(CONFIG_KEY, JSON.stringify(def));
      return def;
    }
    const parsed = JSON.parse(raw);
    const cost = Number(parsed?.avgHumanHourlyCost);
    return { avgHumanHourlyCost: Number.isFinite(cost) && cost > 0 ? cost : DEFAULT_HOURLY_COST };
  } catch {
    return { avgHumanHourlyCost: DEFAULT_HOURLY_COST };
  }
}

/** 写入协同配置 */
function saveConfig(cfg: HumanAiConfig): void {
  try {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
  } catch {
    // 静默失败
  }
}

/** 分钟转可读 */
function formatMinutes(min: number): string {
  if (!min || min <= 0) return "—";
  if (min < 60) return `${min.toFixed(0)}分`;
  return `${(min / 60).toFixed(1)}小时`;
}

/** 金额格式化 */
function formatMoney(yuan: number): string {
  if (!Number.isFinite(yuan)) return "¥0";
  if (yuan >= 10000) return `¥${(yuan / 10000).toFixed(2)}万`;
  return `¥${yuan.toLocaleString("zh-CN", { maximumFractionDigits: 0 })}`;
}

/** 百分比格式化（0..1 → xx.x%） */
function formatPercent(rate: number): string {
  if (!Number.isFinite(rate)) return "0%";
  return `${(rate * 100).toFixed(1)}%`;
}

// ============================================
// 建议规则引擎
// ============================================

function generateRecommendation(
  node: NodeCollaboration
): { type: ExecutorType; reason: string } {
  // 守卫：无人工基准且 AI 表现优秀 → 直接建议 AI
  if (node.humanAvgDuration === 0 && node.aiSuccessRate >= 0.9 && node.aiExecutions > 0) {
    return { type: "ai", reason: "AI成功率高且无人工对照，建议全自动化" };
  }
  // AI成功率>=90%且比人快 → 建议 AI
  if (node.aiSuccessRate >= 0.9 && node.aiAvgDuration < node.humanAvgDuration) {
    return { type: "ai", reason: "AI成功率高且速度快，建议全自动化" };
  }
  // 人工成功率>=95%且 AI成功率<70% → 建议人工
  if (node.humanSuccessRate >= 0.95 && node.aiSuccessRate < 0.7) {
    return { type: "human", reason: "人工执行更可靠，建议人工处理" };
  }
  // 其他 → 混合
  return { type: "hybrid", reason: "AI+人工协同，AI初审+人工复审" };
}

// ============================================
// 协同数据聚合
// ============================================

/** 节点聚合中间结构 */
interface NodeAgg {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  completedCount: number;
  successCount: number;
  durations: number[];
}

/** 单条执行原始记录（用于趋势） */
interface RawExec {
  nodeId: string;
  dateKey: string;
  status: ExecutionStatus;
}

/** 构建协同分析数据 */
function buildCollaboration(
  executions: WorkflowExecution[],
  avgHumanHourlyCost: number
): {
  nodes: NodeCollaboration[];
  stats: CollaborationStats;
  trend: TrendPoint[];
} {
  const nodeMap = new Map<string, NodeAgg>();
  const rawExecs: RawExec[] = [];

  // 全局基准收集（仅纯 ai_call 节点贡献 AI 基准；仅纯 approval 节点贡献人工基准）
  const aiDurations: number[] = [];
  const humanDurations: number[] = [];
  let aiSuccessCount = 0;
  let aiCompletedCount = 0;
  let humanSuccessCount = 0;
  let humanCompletedCount = 0;

  for (const exec of executions) {
    for (const ne of exec.nodeExecutions) {
      const executor = classifyExecutor(ne.nodeType);
      const dateKey = (ne.startTime || exec.startTime || "").slice(0, 10);
      rawExecs.push({ nodeId: ne.nodeId, dateKey, status: ne.status });

      // 节点聚合
      let agg = nodeMap.get(ne.nodeId);
      if (!agg) {
        agg = {
          nodeId: ne.nodeId,
          nodeName: ne.nodeName,
          nodeType: ne.nodeType,
          completedCount: 0,
          successCount: 0,
          durations: [],
        };
        nodeMap.set(ne.nodeId, agg);
      }
      agg.nodeName = ne.nodeName || agg.nodeName;
      agg.nodeType = ne.nodeType || agg.nodeType;

      const isCompleted = COMPLETED_STATUSES.includes(ne.status);
      if (isCompleted) {
        agg.completedCount++;
        if (ne.status === "success") agg.successCount++;
        if (
          typeof ne.duration === "number" &&
          ne.duration > 0 &&
          DURATION_STATUSES.includes(ne.status)
        ) {
          agg.durations.push(ne.duration);
        }
      }

      // 全局基准
      if (isCompleted) {
        if (executor === "ai") {
          aiCompletedCount++;
          if (ne.status === "success") aiSuccessCount++;
          if (
            typeof ne.duration === "number" &&
            ne.duration > 0 &&
            DURATION_STATUSES.includes(ne.status)
          ) {
            aiDurations.push(ne.duration);
          }
        } else if (executor === "human") {
          humanCompletedCount++;
          if (ne.status === "success") humanSuccessCount++;
          if (
            typeof ne.duration === "number" &&
            ne.duration > 0 &&
            DURATION_STATUSES.includes(ne.status)
          ) {
            humanDurations.push(ne.duration);
          }
        }
      }
    }
  }

  // 基准值
  const aiBenchmarkDuration = aiDurations.length
    ? aiDurations.reduce((s, d) => s + d, 0) / aiDurations.length
    : 0;
  const aiBenchmarkSuccessRate = aiCompletedCount ? aiSuccessCount / aiCompletedCount : 0;
  const humanBenchmarkDuration = humanDurations.length
    ? humanDurations.reduce((s, d) => s + d, 0) / humanDurations.length
    : 0;
  const humanBenchmarkSuccessRate = humanCompletedCount
    ? humanSuccessCount / humanCompletedCount
    : 0;

  // 构建 NodeCollaboration[]
  const nodes: NodeCollaboration[] = [];
  for (const agg of nodeMap.values()) {
    const executor = classifyExecutor(agg.nodeType);
    const ownAvg = agg.durations.length
      ? agg.durations.reduce((s, d) => s + d, 0) / agg.durations.length
      : 0;
    const ownSuccess = agg.completedCount ? agg.successCount / agg.completedCount : 0;

    let aiExecutions = 0;
    let humanExecutions = 0;
    let aiAvgDuration = 0;
    let humanAvgDuration = 0;
    let aiSuccessRate = 0;
    let humanSuccessRate = 0;

    if (executor === "ai") {
      aiExecutions = agg.completedCount;
      aiAvgDuration = ownAvg;
      aiSuccessRate = ownSuccess;
      humanAvgDuration = humanBenchmarkDuration;
      humanSuccessRate = humanBenchmarkSuccessRate;
    } else if (executor === "human") {
      humanExecutions = agg.completedCount;
      humanAvgDuration = ownAvg;
      humanSuccessRate = ownSuccess;
      aiAvgDuration = aiBenchmarkDuration;
      aiSuccessRate = aiBenchmarkSuccessRate;
    } else {
      // hybrid：系统自动执行，计入 AI 侧；人工侧用全局基准对比
      aiExecutions = agg.completedCount;
      aiAvgDuration = ownAvg;
      aiSuccessRate = ownSuccess;
      humanAvgDuration = humanBenchmarkDuration;
      humanSuccessRate = humanBenchmarkSuccessRate;
    }

    const aiFaster =
      humanAvgDuration > 0 && aiAvgDuration > 0 && aiAvgDuration < humanAvgDuration;

    // AI 节省时间（分钟）：仅 ai/hybrid 节点，对照人工基准
    let timeSaved = 0;
    if (
      (executor === "ai" || executor === "hybrid") &&
      humanBenchmarkDuration > 0 &&
      ownAvg > 0
    ) {
      timeSaved = Math.max(0, (humanBenchmarkDuration - ownAvg) / 60000) * aiExecutions;
    }

    const base: NodeCollaboration = {
      nodeId: agg.nodeId,
      nodeName: agg.nodeName,
      nodeType: agg.nodeType,
      executorType: executor,
      aiExecutions,
      aiSuccessRate,
      aiAvgDuration,
      humanExecutions,
      humanSuccessRate,
      humanAvgDuration,
      aiFasterThanHuman: aiFaster,
      timeSaved,
      recommendation: "hybrid",
      recommendationReason: "",
    };
    const rec = generateRecommendation(base);
    base.recommendation = rec.type;
    base.recommendationReason = rec.reason;
    nodes.push(base);
  }

  // 统计概览
  const totalNodes = nodes.length;
  const aiNodes = nodes.filter((n) => n.executorType === "ai").length;
  const humanNodes = nodes.filter((n) => n.executorType === "human").length;
  const hybridNodes = nodes.filter((n) => n.executorType === "hybrid").length;
  const totalExecutions = nodes.reduce((s, n) => s + n.aiExecutions + n.humanExecutions, 0);
  const automatedExecutions = nodes.reduce(
    (s, n) => (n.executorType !== "human" ? s + n.aiExecutions : s),
    0
  );
  const aiAutomationRate = totalExecutions ? automatedExecutions / totalExecutions : 0;
  const totalTimeSavedMin = nodes.reduce((s, n) => s + n.timeSaved, 0);
  const totalTimeSaved = totalTimeSavedMin / 60; // 小时
  const estimatedCostSaved = totalTimeSaved * avgHumanHourlyCost;

  const stats: CollaborationStats = {
    totalNodes,
    aiNodes,
    humanNodes,
    hybridNodes,
    aiAutomationRate,
    totalTimeSaved,
    estimatedCostSaved,
    avgHumanHourlyCost,
  };

  // 人力节省趋势（最近 14 天，每日节省小时）
  const nodeAiAvgMap = new Map<string, number>();
  for (const n of nodes) {
    if (n.executorType === "ai" || n.executorType === "hybrid") {
      nodeAiAvgMap.set(n.nodeId, n.aiAvgDuration);
    }
  }
  const days = 14;
  const dayMap = new Map<string, number>();
  const dayLabels = new Map<string, string>();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    dayMap.set(key, 0);
    dayLabels.set(key, `${d.getMonth() + 1}/${d.getDate()}`);
  }
  for (const re of rawExecs) {
    if (!dayMap.has(re.dateKey)) continue;
    if (!COMPLETED_STATUSES.includes(re.status)) continue;
    const nodeAvg = nodeAiAvgMap.get(re.nodeId);
    if (nodeAvg === undefined || nodeAvg <= 0) continue;
    if (humanBenchmarkDuration <= 0) continue;
    const savedMin = Math.max(0, (humanBenchmarkDuration - nodeAvg) / 60000);
    dayMap.set(re.dateKey, (dayMap.get(re.dateKey) || 0) + savedMin);
  }
  const trend: TrendPoint[] = [];
  for (const [dateKey, savedMin] of dayMap) {
    trend.push({
      date: dateKey,
      label: dayLabels.get(dateKey) || dateKey,
      savedHours: savedMin / 60,
    });
  }
  trend.sort((a, b) => (a.date < b.date ? -1 : 1));

  return { nodes, stats, trend };
}

/** 生成人工节点优化建议 */
function buildHumanNodeSuggestions(nodes: NodeCollaboration[]): HumanNodeSuggestion[] {
  const suggestions: HumanNodeSuggestion[] = [];
  const aiBenchmark = nodes
    .filter((n) => n.executorType === "ai" && n.aiAvgDuration > 0)
    .reduce((s, n, _i, arr) => s + n.aiAvgDuration / arr.length, 0);

  for (const n of nodes) {
    if (n.executorType !== "human") continue;
    let suggestion = "可保留人工，定期评估AI替代可行性";
    let priority: HumanNodeSuggestion["priority"] = "low";

    if (n.humanSuccessRate < 0.9) {
      suggestion = "成功率偏低，建议引入AI预审提升可靠性";
      priority = "high";
    } else if (aiBenchmark > 0 && n.humanAvgDuration > aiBenchmark * 1.5) {
      suggestion = "人工耗时显著高于AI基准，建议AI初审+人工复审";
      priority = "high";
    } else if (n.humanExecutions >= 5) {
      suggestion = "高频人工节点，建议部分自动化以释放人力";
      priority = "medium";
    }
    suggestions.push({ nodeId: n.nodeId, nodeName: n.nodeName, suggestion, priority });
  }
  // 高优先级在前
  const order = { high: 0, medium: 1, low: 2 };
  suggestions.sort((a, b) => order[a.priority] - order[b.priority]);
  return suggestions;
}

// ============================================
// 子组件
// ============================================

/** 执行方标签 */
function ExecutorBadge({ type }: { type: ExecutorType }) {
  const cfg = EXECUTOR_CONFIG[type];
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${cfg.bg} ${cfg.border} border`}
      style={{ color: cfg.color }}
    >
      {type === "ai" && <Bot size={9} />}
      {type === "human" && <UserCog size={9} />}
      {type === "hybrid" && <Sparkles size={9} />}
      <span>{cfg.label}</span>
    </span>
  );
}

/** KPI 卡片 */
interface MiniKpiProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  color: string;
  delay?: number;
}

function MiniKpi({ label, value, icon: Icon, color, delay = 0 }: MiniKpiProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
      className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3 overflow-hidden"
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${color}80, transparent)` }}
      />
      <div
        className="absolute -top-8 -right-8 w-16 h-16 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
          filter: "blur(16px)",
        }}
      />
      <div className="flex items-center gap-2 mb-1.5 relative">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30` }}
        >
          <Icon size={12} style={{ color }} />
        </div>
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-[20px] font-mono font-bold text-white tracking-tight leading-none relative">
        {value}
      </div>
    </motion.div>
  );
}

/** 空状态 */
function EmptyState({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="py-8 text-center">
      <div className="text-[12px] font-mono text-gray-600 italic">{text}</div>
      {sub && <div className="text-[10px] font-mono text-gray-700 mt-1.5">{sub}</div>}
    </div>
  );
}

/** 面板容器（带标题） */
function Panel({
  title,
  icon: Icon,
  iconColor,
  extra,
  children,
  delay = 0,
}: {
  title: string;
  icon: LucideIcon;
  iconColor: string;
  extra?: ReactNode;
  children: ReactNode;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={13} style={{ color: iconColor }} />
          <span className="text-[12px] font-medium text-gray-200">{title}</span>
        </div>
        {extra}
      </div>
      {children}
    </motion.div>
  );
}

// ============================================
// 主组件
// ============================================
export default function HumanAICollaboration() {
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [config, setConfig] = useState<HumanAiConfig>({ avgHumanHourlyCost: DEFAULT_HOURLY_COST });
  const [loading, setLoading] = useState(true);

  // 初始化加载
  useEffect(() => {
    setExecutions(loadExecutions());
    setConfig(loadConfig());
    setLoading(false);
  }, []);

  // 跨标签页同步
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === EXECUTIONS_KEY) setExecutions(loadExecutions());
      if (e.key === CONFIG_KEY) setConfig(loadConfig());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // 手动刷新
  const handleRefresh = useCallback(() => {
    setExecutions(loadExecutions());
    setConfig(loadConfig());
  }, []);

  // 更新人工时薪
  const handleCostChange = useCallback((val: number) => {
    const safe = Number.isFinite(val) && val > 0 ? val : DEFAULT_HOURLY_COST;
    const next = { avgHumanHourlyCost: safe };
    setConfig(next);
    saveConfig(next);
  }, []);

  // 聚合分析
  const { nodes, stats, trend } = useMemo(
    () => buildCollaboration(executions, config.avgHumanHourlyCost),
    [executions, config.avgHumanHourlyCost]
  );

  // 人工节点优化建议
  const humanSuggestions = useMemo(() => buildHumanNodeSuggestions(nodes), [nodes]);

  // 节点表（按 AI替代率 desc, 节省时间 desc）
  const sortedNodes = useMemo(() => {
    return [...nodes].sort((a, b) => {
      const aRate = a.aiExecutions + a.humanExecutions > 0 ? a.aiExecutions / (a.aiExecutions + a.humanExecutions) : 0;
      const bRate = b.aiExecutions + b.humanExecutions > 0 ? b.aiExecutions / (b.aiExecutions + b.humanExecutions) : 0;
      if (bRate !== aRate) return bRate - aRate;
      return b.timeSaved - a.timeSaved;
    });
  }, [nodes]);

  // AI vs 人工 对比柱状图数据（取节省时间 Top8）
  const compareData = useMemo(() => {
    return [...nodes]
      .filter((n) => n.aiAvgDuration > 0 || n.humanAvgDuration > 0)
      .sort((a, b) => b.timeSaved - a.timeSaved)
      .slice(0, 8)
      .map((n) => ({
        name: n.nodeName.length > 10 ? n.nodeName.slice(0, 10) + "…" : n.nodeName,
        ai: Number((n.aiAvgDuration / 1000).toFixed(1)),
        human: Number((n.humanAvgDuration / 1000).toFixed(1)),
      }));
  }, [nodes]);

  // 饼图数据
  const pieData = useMemo(
    () => [
      { name: "AI节点", value: stats.aiNodes, color: EXECUTOR_CONFIG.ai.color },
      { name: "人工节点", value: stats.humanNodes, color: EXECUTOR_CONFIG.human.color },
      { name: "混合节点", value: stats.hybridNodes, color: EXECUTOR_CONFIG.hybrid.color },
    ].filter((d) => d.value > 0),
    [stats]
  );

  const hasData = nodes.length > 0;

  // ===== 渲染 =====
  return (
    <div className="space-y-3">
      {/* ===== 顶部标题栏 ===== */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Cpu size={16} className="text-sky-400" />
          <span className="text-[14px] font-medium text-white">人机协同优化</span>
          <span className="text-[10px] font-mono text-gray-600">
            节点 {stats.totalNodes} · 自动化率 {formatPercent(stats.aiAutomationRate)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* 人工时薪配置 */}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02]">
            <Wallet size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-gray-500">人工时薪</span>
            <input
              type="number"
              min={1}
              value={config.avgHumanHourlyCost}
              onChange={(e) => handleCostChange(Number(e.target.value))}
              className="w-16 px-1.5 py-0.5 rounded bg-black/30 border border-white/[0.06] text-[11px] font-mono text-amber-300 focus:outline-none focus:border-amber-500/40 text-right"
            />
            <span className="text-[10px] font-mono text-gray-600">元/h</span>
          </div>
          {/* 刷新 */}
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
            title="刷新数据"
          >
            <RefreshCw size={11} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* ===== 顶部协同概览 KPI ===== */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <MiniKpi label="AI节点" value={stats.aiNodes} icon={Bot} color="#60A5FA" delay={0} />
        <MiniKpi label="人工节点" value={stats.humanNodes} icon={UserCog} color="#FBBF24" delay={0.05} />
        <MiniKpi label="混合节点" value={stats.hybridNodes} icon={Sparkles} color="#A78BFA" delay={0.1} />
        <MiniKpi
          label="AI替代率"
          value={formatPercent(stats.aiAutomationRate)}
          icon={Gauge}
          color="#34D399"
          delay={0.15}
        />
        <MiniKpi
          label="总节省时间"
          value={`${stats.totalTimeSaved.toFixed(1)}h`}
          icon={Clock}
          color="#22D3EE"
          delay={0.2}
        />
        <MiniKpi
          label="预估节省成本"
          value={formatMoney(stats.estimatedCostSaved)}
          icon={Coins}
          color="#F472B6"
          delay={0.25}
        />
      </div>

      {loading ? (
        <div className="py-8 text-center text-[12px] text-gray-500">加载中...</div>
      ) : !hasData ? (
        <Panel title="协同分析" icon={Cpu} iconColor="#60A5FA" delay={0.2}>
          <EmptyState text="暂无执行数据" sub="工作流执行后会自动分析人机协同情况" />
        </Panel>
      ) : (
        <>
          {/* ===== 中部：节点协同分析表 + 对比图 / 饼图 + 趋势图 ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* 左：节点协同分析表 + AI vs 人工对比柱状图 */}
            <div className="lg:col-span-2 space-y-3">
              <Panel
                title="节点协同分析"
                icon={Layers}
                iconColor="#60A5FA"
                delay={0.2}
                extra={
                  <span className="text-[10px] font-mono text-gray-600">
                    共 {nodes.length} 个节点
                  </span>
                }
              >
                <div className="max-h-[260px] overflow-y-auto">
                  {/* 表头 */}
                  <div className="grid grid-cols-12 gap-2 px-2 py-1.5 text-[10px] font-mono text-gray-600 uppercase tracking-wider border-b border-white/[0.04] sticky top-0 bg-[#0a0e1a]/95 backdrop-blur z-10">
                    <div className="col-span-3">节点</div>
                    <div className="col-span-2">执行方</div>
                    <div className="col-span-2 text-right">AI(次/率)</div>
                    <div className="col-span-2 text-right">人工(次/率)</div>
                    <div className="col-span-1 text-right">替代率</div>
                    <div className="col-span-2 text-right">节省/建议</div>
                  </div>
                  {sortedNodes.map((n) => {
                    const total = n.aiExecutions + n.humanExecutions;
                    const rate = total > 0 ? n.aiExecutions / total : 0;
                    return (
                      <div
                        key={n.nodeId}
                        className="grid grid-cols-12 gap-2 px-2 py-1.5 items-center border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors"
                      >
                        <div className="col-span-3 min-w-0">
                          <div className="text-[11px] font-mono text-gray-200 truncate">
                            {n.nodeName}
                          </div>
                          <div className="text-[9px] font-mono text-gray-600">[{n.nodeType}]</div>
                        </div>
                        <div className="col-span-2">
                          <ExecutorBadge type={n.executorType} />
                        </div>
                        <div className="col-span-2 text-right">
                          <div className="text-[10px] font-mono text-sky-300">{n.aiExecutions}</div>
                          <div className="text-[9px] font-mono text-gray-600">
                            {formatPercent(n.aiSuccessRate)}
                          </div>
                        </div>
                        <div className="col-span-2 text-right">
                          <div className="text-[10px] font-mono text-amber-300">
                            {n.humanExecutions}
                          </div>
                          <div className="text-[9px] font-mono text-gray-600">
                            {formatPercent(n.humanSuccessRate)}
                          </div>
                        </div>
                        <div className="col-span-1 text-right">
                          <span
                            className="text-[10px] font-mono"
                            style={{
                              color:
                                rate >= 0.67
                                  ? "#34D399"
                                  : rate >= 0.34
                                  ? "#FBBF24"
                                  : "#F87171",
                            }}
                          >
                            {(rate * 100).toFixed(0)}%
                          </span>
                        </div>
                        <div className="col-span-2 text-right">
                          <div className="text-[10px] font-mono text-cyan-300">
                            {n.timeSaved > 0 ? formatMinutes(n.timeSaved) : "—"}
                          </div>
                          <div
                            className="text-[9px] font-mono"
                            style={{ color: EXECUTOR_CONFIG[n.recommendation].color }}
                          >
                            建议{n.recommendation === "ai" ? "AI" : n.recommendation === "human" ? "人工" : "混合"}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Panel>

              {/* AI vs 人工 对比柱状图 */}
              <Panel
                title="AI vs 人工 耗时对比（秒，Top8 节省）"
                icon={BarChart3}
                iconColor="#22D3EE"
                delay={0.25}
                extra={
                  <div className="flex items-center gap-3 text-[10px] font-mono">
                    <span className="flex items-center gap-1 text-sky-400">
                      <span className="w-2 h-2 rounded-full bg-sky-400" />AI
                    </span>
                    <span className="flex items-center gap-1 text-amber-400">
                      <span className="w-2 h-2 rounded-full bg-amber-400" />人工
                    </span>
                  </div>
                }
              >
                {compareData.length > 0 ? (
                  <div className="h-[220px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={compareData}
                        layout="vertical"
                        margin={{ top: 4, right: 16, left: 4, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} />
                        <XAxis
                          type="number"
                          tick={{ fill: "#6B7280", fontSize: 10, fontFamily: "monospace" }}
                          axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                          tickLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={96}
                          tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                          axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(15,23,42,0.95)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 8,
                            fontSize: 11,
                            fontFamily: "monospace",
                          }}
                          labelStyle={{ color: "#9CA3AF" }}
                          formatter={(value: any, name: any) => [`${value}s`, name === "ai" ? "AI" : "人工"]}
                        />
                        <Bar dataKey="human" fill="#FBBF24" radius={[0, 3, 3, 0]} name="人工" />
                        <Bar dataKey="ai" fill="#60A5FA" radius={[0, 3, 3, 0]} name="AI" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState text="暂无可对比的耗时数据" />
                )}
              </Panel>
            </div>

            {/* 右：AI替代率饼图 + 人力节省趋势图 */}
            <div className="space-y-3">
              <Panel
                title="AI替代率分布"
                icon={PieChartIcon}
                iconColor="#A78BFA"
                delay={0.3}
              >
                {pieData.length > 0 ? (
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                        >
                          {pieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.color}
                              stroke="rgba(10,14,26,0.8)"
                              strokeWidth={1}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            background: "rgba(15,23,42,0.95)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 8,
                            fontSize: 11,
                            fontFamily: "monospace",
                          }}
                          labelStyle={{ color: "#9CA3AF" }}
                          itemStyle={{ color: "#9CA3AF" }}
                          formatter={(value: any, name: any) => [`${value} 个`, String(name)]}
                        />
                        <Legend
                          wrapperStyle={{ fontSize: "10px", fontFamily: "monospace" }}
                          iconType="circle"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState text="暂无节点数据" />
                )}
              </Panel>

              <Panel
                title="人力节省趋势（小时/天，近14天）"
                icon={TrendingUp}
                iconColor="#34D399"
                delay={0.35}
              >
                {trend.some((t) => t.savedHours > 0) ? (
                  <div className="h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradSaved" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#34D399" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#34D399" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: "#6B7280", fontSize: 10, fontFamily: "monospace" }}
                          axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: "#6B7280", fontSize: 10, fontFamily: "monospace" }}
                          axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(15,23,42,0.95)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 8,
                            fontSize: 11,
                            fontFamily: "monospace",
                          }}
                          labelStyle={{ color: "#9CA3AF" }}
                          formatter={(value: any) => [`${Number(value).toFixed(2)}h`, "节省"]}
                        />
                        <Area
                          type="monotone"
                          dataKey="savedHours"
                          stroke="#34D399"
                          strokeWidth={1.5}
                          fill="url(#gradSaved)"
                          name="节省"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState text="暂无节省数据" />
                )}
              </Panel>
            </div>
          </div>

          {/* ===== 底部：最佳实践库 + 人工节点优化建议 ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* 最佳实践库 */}
            <Panel
              title="人机协作最佳实践库"
              icon={ClipboardList}
              iconColor="#60A5FA"
              delay={0.4}
            >
              <div className="space-y-1.5">
                {BEST_PRACTICES.map((bp) => (
                  <motion.div
                    key={bp.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="rounded-lg border border-white/[0.05] bg-white/[0.01] p-2.5 hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Zap size={11} className="text-sky-400" />
                        <span className="text-[11px] font-mono text-gray-200">{bp.scenario}</span>
                      </div>
                      <ExecutorBadge type={bp.recommendedExecutor} />
                    </div>
                    <div className="text-[10px] font-mono text-gray-400 mb-0.5">
                      <CheckCircle2 size={9} className="inline mr-1 text-emerald-400/70" />
                      {bp.reason}
                    </div>
                    <div className="text-[10px] font-mono text-gray-600">
                      <Lightbulb size={9} className="inline mr-1 text-amber-400/70" />
                      案例：{bp.example}
                    </div>
                  </motion.div>
                ))}
              </div>
            </Panel>

            {/* 人工节点优化建议 */}
            <Panel
              title="人工节点优化建议"
              icon={Lightbulb}
              iconColor="#FBBF24"
              delay={0.45}
              extra={
                <span className="text-[10px] font-mono text-gray-600">
                  {humanSuggestions.length} 个待优化
                </span>
              }
            >
              {humanSuggestions.length > 0 ? (
                <div className="space-y-1.5">
                  {humanSuggestions.map((s) => {
                    const cfg =
                      s.priority === "high"
                        ? { color: "#F87171", bg: "bg-red-500/10", border: "border-red-500/30", label: "高" }
                        : s.priority === "medium"
                        ? { color: "#FBBF24", bg: "bg-amber-500/10", border: "border-amber-500/30", label: "中" }
                        : { color: "#9CA3AF", bg: "bg-gray-500/10", border: "border-gray-500/30", label: "低" };
                    return (
                      <motion.div
                        key={s.nodeId}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                        className="rounded-lg border border-white/[0.05] bg-white/[0.01] p-2.5"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <UserCog size={11} className="text-amber-400 shrink-0" />
                            <span className="text-[11px] font-mono text-gray-200 truncate">
                              {s.nodeName}
                            </span>
                          </div>
                          <span
                            className={`shrink-0 px-1.5 py-0.5 rounded text-[9px] font-mono ${cfg.bg} ${cfg.border} border`}
                            style={{ color: cfg.color }}
                          >
                            {cfg.label}
                          </span>
                        </div>
                        <div className="text-[10px] font-mono text-gray-400">
                          <AlertCircle size={9} className="inline mr-1 text-sky-400/70" />
                          {s.suggestion}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState text="暂无人工节点" sub="所有节点已自动化或无人工审批节点" />
              )}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
